import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, formatINR, formatINRAxis, formatINRShort, formatShort, formatDate } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { PageHeader } from "@/components/Shared";
import { FinanceLock } from "@/components/FinanceLock";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, TrendingUp, Receipt, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, Cell, LabelList, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const ORANGE = "#FF5A00";
const FADED = "#FFD3BF";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAILY_MAX_DAYS = 92; // the server's limit for a day-by-day view
const TOOLTIP_STYLE = { borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 };

// Day bars are narrow: plain lakh figures (the chart title says so).
const lakhLabel = (v) => (v / 1e5).toFixed(v >= 1e6 ? 0 : 1);
const thisMonth = () => isoDay(new Date()).slice(0, 7);

/** Revenue above the bar; number of invoices inside its top (above the revenue when the
 * bar is too short). SVG text, so nothing wraps on narrow bars. */
const barLabels = (rows, amount, bright, size = 10) => function BarLabels({ x, y, width, height, index }) {
  const row = rows[index];
  if (!row || !row.revenue) return null;
  const cx = x + width / 2;
  const count = row.invoices.toLocaleString("en-IN");
  const word = row.invoices === 1 ? "inv" : "invs";
  // One line ("156 invs") when the bar is wide enough, else the word under the number.
  const oneLine = width >= (count.length + word.length + 1) * size * 0.62;
  const fits = height >= (oneLine ? size + 8 : 2 * size + 10);
  const fill = fits && bright(row) ? "#FFFFFF" : "#9A3412";
  const top = fits ? y + size + 4 : y - 5 - (oneLine ? 1 : 2) * (size + 3);
  return (
    <g className="font-mono-num" fontSize={size} textAnchor="middle">
      <text x={cx} y={y - 5} fill="#3F3F46" fontWeight={600}>{amount(row.revenue)}</text>
      <text x={cx} y={top} fill={fill}>
        <tspan fontWeight={700}>{count}</tspan>
        {oneLine
          ? <tspan fontWeight={400}> {word}</tspan>
          : <tspan x={cx} dy={size + 1} fontWeight={400} fontSize={size - 1}>{word}</tspan>}
      </text>
    </g>
  );
};

function BarKey() {
  return (
    <span className="inline-flex items-center gap-3 text-[11px] text-neutral-500">
      <span>Above bar: revenue</span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block h-2.5 w-2.5 rounded-[1px]" style={{ background: ORANGE }} />In bar: number of invoices (invs)
      </span>
    </span>
  );
}

const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthName = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
const monthBounds = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return [`${ym}-01`, isoDay(new Date(y, m, 0))];
};
const daysBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 86400000);

// The dates in the address bar, read as a month, a single day or a free range.
function describe(from, to) {
  if (!from && !to) return { label: "All dates" };
  if (from && from === to) return { label: formatDate(from), day: from, month: from.slice(0, 7) };
  if (from && to && from.endsWith("-01") && monthBounds(from.slice(0, 7))[1] === to) {
    return { label: monthName(from.slice(0, 7)), month: from.slice(0, 7), wholeMonth: true };
  }
  return { label: `${from ? formatDate(from) : "Start"} – ${to ? formatDate(to) : "Today"}` };
}

function presets() {
  const today = new Date();
  const fy = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return [
    ["This month", monthBounds(isoDay(today).slice(0, 7))],
    ["Last month", monthBounds(isoDay(new Date(today.getFullYear(), today.getMonth() - 1, 1)).slice(0, 7))],
    ["This FY", [`${fy}-04-01`, `${fy + 1}-03-31`]],
    ["Last FY", [`${fy - 1}-04-01`, `${fy}-03-31`]],
    ["All dates", ["", ""]],
  ];
}

export default function Sales() {
  return (
    <FinanceLock title="Sales">
      <SalesInner />
    </FinanceLock>
  );
}

function SalesInner() {
  const [params, setParams] = useSearchParams();
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const period = describe(from, to);
  // Day bars for a picked month (or day, shown within its month), or a short custom range.
  const dayRange = period.month
    ? monthBounds(period.month)
    : from && to && daysBetween(from, to) >= 0 && daysBetween(from, to) < DAILY_MAX_DAYS ? [from, to] : null;

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState(null);
  const [byCustomer, setByCustomer] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const loads = useRef(0);
  const listLoads = useRef(0);

  const range = useMemo(() => ({ ...(from && { date_from: from }), ...(to && { date_to: to }) }), [from, to]);

  const setRange = (f, t) => {
    if (f && t && f > t) [f, t] = [t, f];
    const next = new URLSearchParams(params);
    f ? next.set("from", f) : next.delete("from");
    t ? next.set("to", t) : next.delete("to");
    setParams(next, { replace: true });
  };

  // A slow answer for dates no longer picked must not overwrite the current ones.
  const loadList = () => {
    const n = ++listLoads.current;
    const query = { limit: from || to ? 2000 : 200, ...range, ...(q && { q }) };
    return api.get("/sales", { params: query }).then(({ data }) => n === listLoads.current && setRows(data));
  };

  const loadAll = () => {
    const n = ++loads.current;
    const keep = (set) => ({ data }) => n === loads.current && set(data);
    if (!dayRange) setDaily(null);
    return Promise.all([
      loadList(),
      api.get("/sales/summary", { params: range }).then(keep(setSummary)),
      api.get("/sales/by-customer", { params: { limit: 15, ...range } }).then(keep(setByCustomer)),
      dayRange && api.get("/sales/daily", { params: { date_from: dayRange[0], date_to: dayRange[1] } }).then(keep(setDaily)),
    ]);
  };
  useEffect(() => {
    loadAll().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line
  }, [from, to]);
  useLiveRefresh(["finance", "customers"], loadAll);

  const pickMonth = (month) => {
    if (!month) return;
    if (period.wholeMonth && period.month === month) setRange("", "");
    else setRange(...monthBounds(month));
  };
  const pickDay = (day) => {
    if (!day) return;
    if (period.day === day) setRange(...monthBounds(day.slice(0, 7)));
    else setRange(day, day);
  };

  const recompute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sales/recompute");
      toast.success(`Revenue on ${data.jobs_with_revenue.toLocaleString("en-IN")} jobs · RM cost on ${data.jobs_with_material_cost.toLocaleString("en-IN")}`);
    } catch (e) { toast.error("Recompute failed"); }
    finally { setBusy(false); }
  };

  const filtered = Boolean(from || to);
  const months = (summary?.monthly || []).filter((m) => m.month && m.month.length >= 7);
  // Bars of the previously picked month stay hidden until the new month's days arrive.
  const days = dayRange && daily?.[0]?.day === dayRange[0] ? daily : null;
  const dayTick = period.month ? (d) => d.slice(8) : (d) => `${d.slice(8)}/${d.slice(5, 7)}`;
  const monthInRange = (m) => (!from || m >= from.slice(0, 7)) && (!to || m <= to.slice(0, 7));

  return (
    <div>
      <PageHeader
        subtitle="Sales Register"
        title="Sales Invoices"
        actions={
          <Button data-testid="recompute-btn" onClick={recompute} disabled={busy} variant="outline" className="rounded-sm">
            <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} />
            Re-link to Jobs
          </Button>
        }
      />
      <div className="p-8 space-y-6">
        <DateFilter from={from} to={to} period={period} onChange={setRange} />

        {summary && (
          <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kpi label={`Revenue · ${period.label}`} value={formatINRShort(summary.total_revenue)} exact={formatINR(summary.total_revenue)} icon={TrendingUp} accent="text-emerald-700" testid="kpi-sales-revenue" />
            <Kpi label="Taxable Value" value={formatINRShort(summary.total_taxable)} exact={formatINR(summary.total_taxable)} />
            <Kpi label="Invoices" value={summary.invoice_count.toLocaleString("en-IN")} sub={`${summary.lines.toLocaleString("en-IN")} lines`} icon={Receipt} testid="kpi-sales-invoices" />
            <Kpi label="Avg value / Invoice" value={formatINRShort(summary.total_revenue / Math.max(1, summary.invoice_count))} exact={formatINR(summary.total_revenue / Math.max(1, summary.invoice_count))} testid="kpi-avg-invoice" />
            <Kpi label="Avg value / Job" value={summary.jobs ? formatINRShort(summary.job_value / summary.jobs) : "—"}
              sub={`${summary.jobs.toLocaleString("en-IN")} jobs billed`} testid="kpi-avg-job" />
            <Kpi label="Avg value / Label" value={summary.labels ? `₹${(summary.label_value / summary.labels).toFixed(2)}` : "—"}
              sub={`${formatShort(summary.labels)} labels billed`} testid="kpi-avg-label" />
          </section>
        )}

        {months.length > 0 && (
          <div className="border border-neutral-200 bg-white p-5" data-testid="sales-monthly">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <div className="label-caps">Monthly Revenue</div>
                <BarKey />
              </div>
              <div className="text-xs text-neutral-500">
                {months.some((m) => m.month === thisMonth()) && `${monthName(thisMonth())} is month to date · `}
                Click a month to see its days and invoices
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={months} margin={{ top: 22, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#eee" strokeDasharray="2 2" vertical={false} />
                  <XAxis dataKey="month" fontSize={11} stroke="#71717A" interval={0} />
                  <YAxis fontSize={11} stroke="#71717A" tickFormatter={formatINRAxis} />
                  <Tooltip
                    cursor={{ fill: "#F4F4F5" }}
                    labelFormatter={(m) => (m === thisMonth() ? `${monthName(m)} · month to date` : monthName(m))}
                    formatter={(v, _n, item) => [`${formatINR(v)} · ${item.payload.invoices.toLocaleString("en-IN")} invoices`, "Revenue"]}
                    contentStyle={TOOLTIP_STYLE}
                  />
                  <Bar dataKey="revenue" cursor="pointer" onClick={(bar) => pickMonth((bar.payload || bar).month)}>
                    <LabelList dataKey="revenue" content={barLabels(months, (v) => formatShort(v, 1), (m) => !filtered || monthInRange(m.month))} />
                    {months.map((m) => (
                      <Cell key={m.month} fill={!filtered || monthInRange(m.month) ? ORANGE : FADED} data-testid={`sales-month-${m.month}`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {days && (
          <div className="border border-neutral-200 bg-white p-5" data-testid="sales-daily">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <div className="label-caps">Daily Revenue · {period.month ? monthName(period.month) : period.label} · ₹ lakh</div>
                <BarKey />
              </div>
              <div className="text-xs text-neutral-500">
                {period.day ? "Click the day again for the whole month" : "Click a day to see its invoices"}
              </div>
            </div>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={days} margin={{ top: 22, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#eee" strokeDasharray="2 2" vertical={false} />
                  <XAxis dataKey="day" fontSize={11} stroke="#71717A" tickFormatter={dayTick} interval={days.length > 31 ? "preserveStartEnd" : 0} />
                  <YAxis fontSize={11} stroke="#71717A" tickFormatter={formatINRAxis} />
                  <Tooltip
                    cursor={{ fill: "#F4F4F5" }}
                    labelFormatter={(d) => formatDate(d)}
                    formatter={(v, _n, item) => [`${formatINR(v)} · ${item.payload.invoices.toLocaleString("en-IN")} invoices`, "Revenue"]}
                    contentStyle={TOOLTIP_STYLE}
                  />
                  <Bar dataKey="revenue" cursor="pointer" onClick={(bar) => pickDay((bar.payload || bar).day)}>
                    {days.length <= 31 && <LabelList dataKey="revenue" content={barLabels(days, lakhLabel, (d) => !period.day || period.day === d.day, 9)} />}
                    {days.map((d) => (
                      <Cell key={d.day} fill={!period.day || period.day === d.day ? ORANGE : FADED} data-testid={`sales-day-${d.day}`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {byCustomer.length > 0 && (
          <div className="border border-neutral-200 bg-white">
            <div className="px-5 py-4 border-b border-neutral-200 label-caps">Top Customers (Party Units) · {period.label}</div>
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 label-caps">
                <tr>
                  <th className="text-left px-4 py-2.5">Party Name (Unit)</th>
                  <th className="text-right px-4 py-2.5">Invoices</th>
                  <th className="text-right px-4 py-2.5">Revenue</th>
                  <th className="text-right px-4 py-2.5">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {byCustomer.map((c) => (
                  <tr key={c.party_name} className="border-t border-neutral-100 data-row">
                    <td className="px-4 py-2.5 font-medium">{c.party_name}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{c.invoices}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(c.revenue)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-neutral-500">
                      {summary?.total_revenue ? ((c.revenue / summary.total_revenue) * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="border border-neutral-200 bg-white">
          <div className="px-5 py-4 border-b border-neutral-200 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="label-caps">Invoices · {period.label}</div>
              {summary && filtered && !q && (
                <div className="text-xs text-neutral-500 mt-1" data-testid="sales-list-count">
                  {summary.invoice_count.toLocaleString("en-IN")} invoices · {summary.lines.toLocaleString("en-IN")} lines
                  {rows.length < summary.lines && ` · first ${rows.length.toLocaleString("en-IN")} shown`}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
                <Input
                  data-testid="sales-search"
                  placeholder="Invoice, party, product…"
                  className="pl-8 rounded-sm w-64"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadList()}
                />
              </div>
              <Button variant="outline" onClick={loadList} className="rounded-sm">Apply</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sales-table">
              <thead className="bg-neutral-50 label-caps">
                <tr>
                  <th className="text-left px-4 py-2.5">Invoice #</th>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Party</th>
                  <th className="text-left px-4 py-2.5">Description</th>
                  <th className="text-right px-4 py-2.5">Qty</th>
                  <th className="text-right px-4 py-2.5">Rate</th>
                  <th className="text-right px-4 py-2.5">Job Value</th>
                  <th className="text-right px-4 py-2.5">Tax %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-neutral-100 data-row">
                    <td className="px-4 py-2.5 font-mono-num text-xs">{r.invoice_number}</td>
                    <td className="px-4 py-2.5 text-xs text-neutral-600">{formatDate(r.invoice_date)}</td>
                    <td className="px-4 py-2.5">{r.party_name}</td>
                    <td className="px-4 py-2.5 text-neutral-600 max-w-xs truncate" title={r.description}>{r.description}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{r.quantity?.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(r.unit_price)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{formatINR(r.job_value)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-neutral-500">{r.tax_rate ? `${(r.tax_rate * 100).toFixed(0)}%` : "—"}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-neutral-400">
                      {filtered ? `No invoices on ${period.label}.` : "No sales invoices match. Import a Sales file first."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function DateFilter({ from, to, period, onChange }) {
  return (
    <div className="border border-neutral-200 bg-white px-5 py-4 flex flex-wrap items-end gap-3" data-testid="sales-date-filter">
      <div>
        <label className="label-caps block mb-1">From</label>
        <Input data-testid="sales-from" type="date" value={from} max={to || undefined} onChange={(e) => onChange(e.target.value, to)} className="rounded-sm w-40" />
      </div>
      <div>
        <label className="label-caps block mb-1">To</label>
        <Input data-testid="sales-to" type="date" value={to} min={from || undefined} onChange={(e) => onChange(from, e.target.value)} className="rounded-sm w-40" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {presets().map(([label, [f, t]]) => {
          const on = f === from && t === to;
          return (
            <Button
              key={label}
              size="sm"
              variant={on ? "default" : "outline"}
              onClick={() => onChange(f, t)}
              className={`rounded-sm ${on ? "bg-[#FF5A00] hover:bg-[#e04f00]" : ""}`}
              data-testid={`sales-preset-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {label}
            </Button>
          );
        })}
      </div>
      <div className="ml-auto flex items-center gap-2 text-sm text-neutral-600">
        Showing <b className="text-neutral-900" data-testid="sales-period">{period.label}</b>
        {(from || to) && (
          <Button variant="ghost" size="sm" onClick={() => onChange("", "")} className="rounded-sm h-7 px-2" data-testid="sales-clear-dates">
            <X size={14} className="mr-1" />Clear
          </Button>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, exact, sub, icon: Icon, accent, testid }) {
  return (
    <div className="border border-neutral-200 bg-white px-4 py-3" data-testid={testid} title={exact}>
      <div className="flex items-start justify-between gap-2">
        <div className="label-caps text-[10px] leading-tight">{label}</div>
        {Icon && <Icon size={13} className="text-neutral-400 shrink-0" />}
      </div>
      <div className={`font-mono-num text-lg font-bold mt-1.5 tracking-tight ${accent || "text-neutral-900"}`}>{value}</div>
      {(sub || exact) && <div className="text-[11px] text-neutral-500 font-mono-num mt-0.5 truncate">{sub || exact}</div>}
    </div>
  );
}
